import { Link } from 'react-router-dom';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Facebook, 
  Instagram, 
  Linkedin,
  ArrowRight
} from 'lucide-react';
import { useData } from '@/context/DataContext';

export const Footer = () => {
  const dataCtx = useData();
  const logoUrl = '/assets/glc-logo.webp';
  const socials = {
    facebook: dataCtx?.data?.company?.social_facebook,
    instagram: dataCtx?.data?.company?.social_instagram,
    linkedin: dataCtx?.data?.company?.social_linkedin,
  };

  return (
    <footer className="bg-slate-950 text-slate-400" data-testid="footer">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              {logoUrl ? (
                <img src={logoUrl} alt="GLC logo" width="226" height="70" loading="lazy" className="h-12 w-auto object-contain" />
              ) : (
                <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center" />
              )}
            </Link>
            <p className="text-slate-400 mb-6 max-w-sm leading-relaxed">
              Linking South African homes and businesses to reliable, cost-effective 
              solar and alternative energy solutions since 2020.
            </p>
            <div className="flex gap-4">
              {socials.facebook && (
                <a 
                  href={socials.facebook}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors"
                  aria-label="Facebook"
                  data-testid="social-facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {socials.instagram && (
                <a 
                  href={socials.instagram}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors"
                  aria-label="Instagram"
                  data-testid="social-instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {socials.linkedin && (
                <a 
                  href={socials.linkedin}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors"
                  aria-label="LinkedIn"
                  data-testid="social-linkedin"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Services</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/services#energy-storage" className="text-slate-400 hover:text-emerald-400 transition-colors inline-flex items-center gap-1 group">
                  Solar Installations
                  <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              </li>
              <li>
                <Link to="/services#energy-storage" className="text-slate-400 hover:text-emerald-400 transition-colors inline-flex items-center gap-1 group">
                  Battery Backup
                  <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              </li>
              <li>
                <Link to="/solar-installers-western-cape" className="text-slate-400 hover:text-emerald-400 transition-colors inline-flex items-center gap-1 group">
                  SSEG Applications
                  <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              </li>
              <li><Link to="/services#maintenance" className="text-slate-400 hover:text-emerald-400 transition-colors">Solar Maintenance</Link></li>
              <li><Link to="/solar-installers-western-cape" className="text-slate-400 hover:text-emerald-400 transition-colors">Commercial Solar</Link></li>
              <li><Link to="/solar-installers-western-cape" className="text-slate-400 hover:text-emerald-400 transition-colors">Residential Solar</Link></li>
            </ul>
          </div>

          {/* Service Area Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Solar Installation Areas</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/solar-installers-western-cape" className="text-slate-400 hover:text-emerald-400 transition-colors inline-flex items-center gap-1 group">
                  Western Cape
                  <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              </li>
              <li>
                <Link to="/solar-installers-cape-town" className="text-slate-400 hover:text-emerald-400 transition-colors inline-flex items-center gap-1 group">
                  Cape Town
                  <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              </li>
              <li>
                <Link to="/solar-installers-paarl" className="text-slate-400 hover:text-emerald-400 transition-colors inline-flex items-center gap-1 group">
                  Paarl
                  <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              </li>
              <li>
                <Link to="/solar-installers-hermanus" className="text-slate-400 hover:text-emerald-400 transition-colors inline-flex items-center gap-1 group">
                  Hermanus
                  <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Products Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Products</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/products?brand=foxess" className="text-slate-400 hover:text-emerald-400 transition-colors inline-flex items-center gap-1 group">
                  Fox ESS
                  <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              </li>
              <li>
                <Link to="/products?brand=sunsynk" className="text-slate-400 hover:text-emerald-400 transition-colors inline-flex items-center gap-1 group">
                  Sunsynk
                  <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              </li>
              <li>
                <Link to="/products?brand=jasolar" className="text-slate-400 hover:text-emerald-400 transition-colors inline-flex items-center gap-1 group">
                  JA Solar
                  <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:0686801986"
                  className="flex items-start gap-3 text-slate-400 hover:text-emerald-400 transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-5 h-5 mt-0.5 shrink-0" />
                  <span>068 680 1986</span>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:admin@greenlightcity.co.za"
                  className="flex items-start gap-3 text-slate-400 hover:text-emerald-400 transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-5 h-5 mt-0.5 shrink-0" />
                  <span>admin@greenlightcity.co.za</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-slate-400">
                  <MapPin className="w-5 h-5 mt-0.5 shrink-0" />
                  <span>South Africa</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800/50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
            <p className="text-slate-500">
              © {new Date().getFullYear()} Green Light City. All rights reserved.
            </p>
            <a
              href="https://pixelpanda.co.za"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-slate-300 transition-colors"
            >
              Built by Pixel Panda Developments
            </a>
            <div className="flex gap-6">
              <Link to="/faq" className="text-slate-500 hover:text-slate-300 transition-colors">
                FAQ
              </Link>
              <Link to="/contact" className="text-slate-500 hover:text-slate-300 transition-colors">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
